'use client';

import { useState, useEffect, useMemo } from 'react';
import { useNumberInput } from '@shared/ui';

interface UseProductCountProps {
    productQuantity: number;
    product: Product | null;
    selectedProductDetailId: string | null;
    onChange: (value: number) => void;
}

const leastProductQuantity = 1;

export function useProductCount(props: UseProductCountProps) {
    const { productQuantity = 1, onChange, product, selectedProductDetailId } = props;

    const [inputValue, setInputValue] = useState(productQuantity);

    const [outOfStock, setOutOfStock] = useState(false);

    const maxStock = useMemo(() => {
        if (!product) {
            return 0;
        }

        if (product.spec_details && product.spec_details.length > 0) {
            const specDetail = product.spec_details.find(
                (i) => i.id === selectedProductDetailId
            );

            if (specDetail) {
                return specDetail.stock;
            }

            return 0;
        }

        return product.total_stock ?? 0;
    }, [product, selectedProductDetailId]);

    const { getInputProps, getIncrementButtonProps, getDecrementButtonProps } =
        useNumberInput({
            step: 1,
            value: inputValue,
            min: leastProductQuantity,
            precision: 0,
            max: maxStock,
            onChange(_, valueAsNumber) {
                const newVal = Math.max(valueAsNumber, 1);

                setInputValue(newVal);

                onChange(newVal);
            },
        });

    const inc = getIncrementButtonProps();

    const dec = getDecrementButtonProps();

    const input = getInputProps();

    useEffect(() => {
        let timer: NodeJS.Timeout;

        const upcomingOutOfStock = maxStock <= productQuantity || maxStock === 0;

        setOutOfStock(upcomingOutOfStock);

        const newVal = Math.max(maxStock, 1);

        if (maxStock <= productQuantity && inputValue !== newVal) {
            setInputValue(newVal);

            onChange(newVal);
        }

        return () => {
            clearTimeout(timer);
        };
    }, [maxStock, productQuantity, setInputValue, onChange, inputValue]);

    return {
        inc,
        dec,
        input,
        outOfStock,
    };
}
